import React from 'react';
import { Building, Mail, Phone, Edit, Trash2 } from 'lucide-react';

/**
 * CRMBoard — Kanban-style pipeline view.
 *
 * Props:
 *   stages: [{id, label, color, is_archived, is_won, is_lost}]
 *   grouped: {stageId: [contacts]}
 *   showArchived: bool — whether to render archived (closed) stages
 *   onEdit(contact), onDelete(id), onDrop(contactId, newStageId)
 *
 * Drag/drop uses HTML5 DnD — same pattern as the previous inline board,
 * pulled out so the CRM.js container can host multiple views.
 */
export default function CRMBoard({ stages, grouped, showArchived, onEdit, onDelete, onDrop, filterQuery }) {
  const [draggedId, setDraggedId] = React.useState(null);

  const visibleStages = stages.filter((s) => showArchived || !s.is_archived);

  const filter = (list) => {
    if (!filterQuery) return list;
    const q = filterQuery.toLowerCase();
    return list.filter(
      (c) =>
        c.name?.toLowerCase().includes(q) ||
        c.email?.toLowerCase().includes(q) ||
        c.company?.toLowerCase().includes(q),
    );
  };

  const handleDrop = (stageId) => {
    if (!draggedId) return;
    onDrop(draggedId, stageId);
    setDraggedId(null);
  };

  return (
    <div className="flex-1 overflow-x-auto overflow-y-hidden p-6" data-testid="crm-board-view">
      <div className="flex gap-4 min-h-[500px]">
        {visibleStages.map((stage) => {
          const list = filter(grouped[stage.id] || []);
          const accent = stage.is_won
            ? 'border-emerald-500/30'
            : stage.is_lost
              ? 'border-rose-500/30'
              : 'border-white/5';
          return (
            <div
              key={stage.id}
              className={`w-[280px] flex-shrink-0 flex flex-col bg-brand-surface rounded-xl border ${accent} relative overflow-hidden`}
              onDragOver={(e) => e.preventDefault()}
              onDrop={() => handleDrop(stage.id)}
              data-testid={`stage-${stage.id}`}
            >
              {stage.is_won && <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/5 to-transparent pointer-events-none" />}
              {stage.is_lost && <div className="absolute inset-0 bg-gradient-to-b from-rose-500/5 to-transparent pointer-events-none" />}

              <div className="px-4 py-3 border-b border-white/5 flex items-center justify-between relative">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 rounded-full" style={{ backgroundColor: stage.color }} />
                  <span
                    className={`text-sm font-semibold ${
                      stage.is_won ? 'text-emerald-300' : stage.is_lost ? 'text-rose-300' : 'text-white'
                    }`}
                  >
                    {stage.label}
                  </span>
                </div>
                <span className={`text-xs ${stage.is_won ? 'text-emerald-300 font-medium' : stage.is_lost ? 'text-rose-300' : 'text-gray-400'}`}>
                  {list.length}
                  {stage.is_won ? ' ✓' : ''}
                  {stage.is_lost ? ' ✗' : ''}
                </span>
              </div>

              <div className="flex-1 overflow-y-auto p-3 space-y-2 relative">
                {list.map((contact) => (
                  <div
                    key={contact.id}
                    draggable
                    onDragStart={() => setDraggedId(contact.id)}
                    className={`p-3 rounded-lg cursor-grab active:cursor-grabbing transition-all hover:-translate-y-0.5 hover:shadow-lg hover:shadow-brand-gold/10 ${
                      stage.is_won
                        ? 'bg-emerald-500/5 border border-emerald-500/20'
                        : stage.is_lost
                          ? 'bg-rose-500/5 border border-rose-500/20'
                          : 'bg-brand-surface2 border border-white/5 hover:border-brand-gold/30'
                    } ${draggedId === contact.id ? 'opacity-50' : ''}`}
                    data-testid={`contact-${contact.id}`}
                  >
                    <div className="flex items-start justify-between mb-1">
                      <p className="text-sm font-medium text-white truncate">{contact.name}</p>
                      <div className="flex gap-1 flex-shrink-0 ml-2">
                        <button
                          onClick={() => onEdit(contact)}
                          className="p-1 text-gray-500 hover:text-brand-gold"
                          data-testid={`edit-${contact.id}`}
                        >
                          <Edit className="w-3 h-3" />
                        </button>
                        <button
                          onClick={() => onDelete(contact.id)}
                          className="p-1 text-gray-500 hover:text-rose-400"
                          data-testid={`delete-${contact.id}`}
                        >
                          <Trash2 className="w-3 h-3" />
                        </button>
                      </div>
                    </div>
                    {contact.company && (
                      <p className="text-[11px] text-gray-400 mb-2 flex items-center gap-1">
                        <Building className="w-3 h-3" /> {contact.company}
                      </p>
                    )}
                    {contact.email && (
                      <p className="text-[11px] text-gray-500 flex items-center gap-1 truncate">
                        <Mail className="w-3 h-3 flex-shrink-0" /> {contact.email}
                      </p>
                    )}
                    {contact.phone && (
                      <p className="text-[11px] text-gray-500 flex items-center gap-1">
                        <Phone className="w-3 h-3" /> {contact.phone}
                      </p>
                    )}
                    {contact.source === 'landing_page' && (
                      <span className="inline-block mt-2 px-1.5 py-0.5 rounded bg-brand-gold/20 text-brand-gold text-[10px] font-medium">
                        From Profile
                      </span>
                    )}
                  </div>
                ))}
                {list.length === 0 && (
                  <p className="text-xs text-gray-500 text-center py-6">
                    {stage.is_archived ? 'Nothing archived here yet' : `Drop contacts to ${stage.label.toLowerCase()}`}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
